import { adminHasAnyPermission, getPermissionCodes } from './menu';
import { isWhatsAppConnected } from '../services/whatsapp';

export { isWhatsAppConnected };

export const WHATSAPP_PERMISSIONS = {
  PARENT: 'WHATSAPP',
  DASHBOARD: 'WHATSAPP_DASHBOARD',
  TEMPLATE: 'WHATSAPP_TEMPLATE',
  TEMPLATE_ADD: 'WHATSAPP_TEMPLATE_ADD',
  CAMPAIGN: 'WHATSAPP_CAMPAIGN',
  CAMPAIGN_ADD: 'WHATSAPP_CAMPAIGN_ADD',
  INBOX: 'WHATSAPP_INBOX',
  PHONEBOOK: 'WHATSAPP_PHONEBOOK',
  PHONEBOOK_ADD: 'WHATSAPP_PHONEBOOK_ADD',
};

/** JewelleryXGuru-compatible codes object */
export const WHATSAPP_PERMISSION_CODES = {
  WHATSAPP: WHATSAPP_PERMISSIONS.PARENT,
  DASHBOARD: WHATSAPP_PERMISSIONS.DASHBOARD,
  TEMPLATE: WHATSAPP_PERMISSIONS.TEMPLATE,
  TEMPLATE_ADD: WHATSAPP_PERMISSIONS.TEMPLATE_ADD,
  CAMPAIGN: WHATSAPP_PERMISSIONS.CAMPAIGN,
  CAMPAIGN_ADD: WHATSAPP_PERMISSIONS.CAMPAIGN_ADD,
  INBOX: WHATSAPP_PERMISSIONS.INBOX,
  PHONEBOOK: WHATSAPP_PERMISSIONS.PHONEBOOK,
  PHONEBOOK_ADD: WHATSAPP_PERMISSIONS.PHONEBOOK_ADD,
};

export const WA_PERMISSION_DENIED = 'You do not have permission to access this page.';

/** Default landing order when opening /whatsapp */
export const WHATSAPP_LANDING_PATHS = [
  { path: '/whatsapp/dashboard', permission: WHATSAPP_PERMISSIONS.DASHBOARD },
  { path: '/whatsapp/phonebooks', permission: WHATSAPP_PERMISSIONS.PHONEBOOK },
  { path: '/whatsapp/templates', permission: WHATSAPP_PERMISSIONS.TEMPLATE },
  { path: '/whatsapp/campaigns', permission: WHATSAPP_PERMISSIONS.CAMPAIGN },
  { path: '/whatsapp/inbox', permission: WHATSAPP_PERMISSIONS.INBOX },
];

const isAdminRole = (admin) => {
  if (!admin) return false;
  const single = String(admin.role_code || admin.role || '');
  if (single.toLowerCase() === 'admin' || single === 'super_admin' || single === 'ADMIN_SUPER') {
    return true;
  }
  const roles = admin.roles || admin.role_codes || [];
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => {
    const code = String(typeof r === 'string' ? r : r?.code || '').toLowerCase();
    return code === 'admin' || code === 'super_admin' || code === 'admin_super';
  });
};

export const canAccessWhatsApp = (admin) => {
  if (isAdminRole(admin)) return true;
  return adminHasAnyPermission(admin, [
    WHATSAPP_PERMISSIONS.PARENT,
    WHATSAPP_PERMISSIONS.DASHBOARD,
    WHATSAPP_PERMISSIONS.TEMPLATE,
    WHATSAPP_PERMISSIONS.CAMPAIGN,
    WHATSAPP_PERMISSIONS.INBOX,
    WHATSAPP_PERMISSIONS.PHONEBOOK,
  ]);
};

/**
 * Page access: specific code, umbrella WHATSAPP, or admin role.
 */
export const hasWhatsAppPagePermission = (admin, pagePermission) => {
  if (isAdminRole(admin)) return true;
  if (!pagePermission) return canAccessWhatsApp(admin);
  return adminHasAnyPermission(admin, [pagePermission, WHATSAPP_PERMISSIONS.PARENT]);
};

/**
 * Permission check used by extracted WhatsApp components.
 * Reads current admin from session storage / AuthContext via getPermissionCodes on a stub.
 * Prefer passing admin when available; falls back to token payload permissions in localStorage.
 */
export const hasWhatsAppPermission = (code, admin = null) => {
  if (admin) return hasWhatsAppPagePermission(admin, code);
  try {
    const raw = localStorage.getItem('admin_data')
      || localStorage.getItem('sanduk_admin')
      || localStorage.getItem('admin')
      || localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      return hasWhatsAppPagePermission(parsed, code);
    }
  } catch {
    /* ignore */
  }
  // If no admin snapshot, allow (gate already enforced at route level)
  return true;
};

export const getDefaultWhatsAppPath = (admin) => {
  for (const item of WHATSAPP_LANDING_PATHS) {
    if (hasWhatsAppPagePermission(admin, item.permission)) return item.path;
  }
  return null;
};

export const getWhatsAppRoutePermission = (pathname) => {
  const path = (pathname || '').replace(/\/$/, '') || '/';
  if (path.startsWith('/whatsapp/dashboard')) return WHATSAPP_PERMISSIONS.DASHBOARD;
  if (path.startsWith('/whatsapp/templates')) return WHATSAPP_PERMISSIONS.TEMPLATE;
  if (path.startsWith('/whatsapp/campaigns')) return WHATSAPP_PERMISSIONS.CAMPAIGN;
  if (path.startsWith('/whatsapp/inbox')) return WHATSAPP_PERMISSIONS.INBOX;
  if (path.startsWith('/whatsapp/phonebooks')) return WHATSAPP_PERMISSIONS.PHONEBOOK;
  if (path.startsWith('/whatsapp/settings')) return WHATSAPP_PERMISSIONS.PARENT;
  return null;
};

export const isWhatsAppPath = (pathname) => (pathname || '').startsWith('/whatsapp');

export const validateWhatsAppSetupForm = (form, settings) => {
  const errors = {};
  if (!form.phone_number_id?.trim()) errors.phone_number_id = 'Phone Number ID is required';
  if (!form.waba_id?.trim()) errors.waba_id = 'WABA ID is required';
  if (!form.access_token?.trim() && !settings?.hasAccessToken) {
    errors.access_token = 'Meta Access Token is required';
  }
  return errors;
};

export const buildWhatsAppSavePayload = (form) => {
  const phone_number_id = form.phone_number_id.trim();
  const waba_id = form.waba_id.trim();
  const app_id = form.app_id.trim();
  const access_token = form.access_token.trim();
  const app_secret = form.app_secret.trim();
  const webhook_verify_token = form.webhook_verify_token.trim();

  const payload = {
    phone_number_id,
    waba_id,
    api_version: 'v21.0',
    is_default: 0,
  };
  if (access_token) payload.access_token = access_token;
  if (app_id) payload.app_id = app_id;
  if (app_secret) payload.app_secret = app_secret;
  if (webhook_verify_token) payload.webhook_verify_token = webhook_verify_token;
  return payload;
};

export { getPermissionCodes };
